/**
 * recently-viewed-section.js
 * ─────────────────────────────────────────────────────────────────────────────
 * PURPOSE
 *   Drives the recently-viewed-section.liquid section. Extends the Horizon
 *   theme's built-in recently viewed product tracking (capped at 4 items) to
 *   store up to EXTENDED_MAX IDs, then fetches and renders the section with
 *   real product data via Shopify's section rendering API.
 *
 * HOW IT WORKS
 *
 *   1. LOCALSTORAGE PATCH
 *      The theme writes recently viewed product IDs to `viewedProducts`
 *      (capped at 4). This module patches localStorage.setItem to mirror
 *      every write to a parallel key `viewedProductsExtended` (capped at
 *      EXTENDED_MAX = 9). An internal flag (_internalWrite) prevents the
 *      patch from re-triggering on its own writes.
 *
 *   2. BACKFILL
 *      On first load, any IDs already in `viewedProducts` are merged into
 *      `viewedProductsExtended` so returning visitors see results immediately
 *      without needing to re-visit products under the new system.
 *
 *   3. ID ORDERING (getOrderedIds)
 *      The theme's `viewedProducts` list always has the current product at
 *      index 0 (addProduct runs before this module). getOrderedIds() builds
 *      a merged list — theme list first (preserving current-product-at-0),
 *      supplemented by older IDs from the extended list.
 *
 *   4. SECTION RENDER (renderRecentlyViewedSection)
 *      - Reads columns and maxItems from data attributes on the section element.
 *      - If offset_current_product is enabled, slices off index 0 (current
 *        product) before building the search query.
 *      - Sends up to maxItems IDs to /search as id:A OR id:B query terms,
 *        which causes Shopify to re-render the section with search.performed=true
 *        and search.results populated with matching products.
 *      - Uses document.adoptNode() + replaceChildren() (not innerHTML) to swap
 *        the rendered HTML into the page — this correctly triggers connectedCallback
 *        on custom elements like slideshow-component and product-card-link.
 *      - Hoists any <style> tags from the rendered fragment to <head>, since
 *        styles injected via innerHTML are inert in browsers.
 *      - Sets --slide-width directly on slideshow-component via style.setProperty()
 *        to override the theme's default 60vw value, calculated from the real
 *        container width so exactly `columns` slides are visible per page.
 *
 * KEY CONSTANTS
 *   THEME_KEY    — localStorage key used by the Horizon theme (do not change)
 *   EXTENDED_KEY — localStorage key used by this module
 *   EXTENDED_MAX — maximum IDs to store; set to columns + 3 to account for
 *                  the offset slot and provide a buffer for unavailable products.
 *                  Must match max_items in recently-viewed-section.liquid.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { RecentlyViewed } from './recently-viewed-products.js';

// ─── Extended storage ─────────────────────────────────────────────────────────
// The theme hard-caps viewedProducts at 4 IDs. We maintain a parallel key
// (viewedProductsExtended) with a cap of 8, kept in sync via a localStorage
// patch. The patch uses an internal flag to avoid re-triggering on our own
// writes, preventing infinite loops and spurious re-renders.

const THEME_KEY    = 'viewedProducts';
const EXTENDED_KEY = 'viewedProductsExtended';
const EXTENDED_MAX = 9; // stores 1 extra to account for current product offset

// Flag set while we are writing to EXTENDED_KEY so the patched setItem
// ignores that write and doesn't trigger another merge cycle.
let _internalWrite = false;

if (!localStorage.__recentlyViewedPatched) {
  const _origSet    = localStorage.setItem.bind(localStorage);
  const _origRemove = localStorage.removeItem.bind(localStorage);

  localStorage.setItem = function (key, value) {
    _origSet(key, value);

    // Only mirror writes to the theme key, and never our own writes.
    if (key !== THEME_KEY || _internalWrite) return;

    try {
      const incoming = JSON.parse(value);
      if (!Array.isArray(incoming) || !incoming.length) return;

      let extended = [];
      try {
        const stored = localStorage.getItem(EXTENDED_KEY);
        if (stored) extended = JSON.parse(stored);
      } catch {}

      const merged = [
        ...incoming,
        ...extended.filter((id) => !incoming.includes(id)),
      ].slice(0, EXTENDED_MAX);

      _internalWrite = true;
      _origSet(EXTENDED_KEY, JSON.stringify(merged));
      _internalWrite = false;
    } catch {}
  };

  localStorage.removeItem = function (key) {
    _origRemove(key);
    if (key === THEME_KEY) {
      try { _origRemove(EXTENDED_KEY); } catch {}
    }
  };

  localStorage.__recentlyViewedPatched = true;
}

// ─── Backfill ─────────────────────────────────────────────────────────────────
// On first load, seed the extended list from the theme's list so returning
// visitors see results immediately without needing to re-visit products.
(function backfill() {
  try {
    const themeRaw = localStorage.getItem(THEME_KEY);
    const extRaw   = localStorage.getItem(EXTENDED_KEY);
    const theme    = themeRaw ? JSON.parse(themeRaw) : [];
    const ext      = extRaw   ? JSON.parse(extRaw)   : [];

    const merged = [
      ...ext,
      ...theme.filter((id) => !ext.includes(id)),
    ].slice(0, EXTENDED_MAX);

    if (merged.length !== ext.length || merged.some((id, i) => id !== ext[i])) {
      _internalWrite = true;
      localStorage.setItem(EXTENDED_KEY, JSON.stringify(merged));
      _internalWrite = false;
    }
  } catch {}
})();

// ─── Read helper ──────────────────────────────────────────────────────────────
// Returns IDs in the correct order for the section:
//   - Theme list first (current product is always index 0, added by addProduct)
//   - Extended list supplements with older items beyond the theme's 4-item cap
// This ensures offset_current_product correctly skips index 0.
function getOrderedIds() {
  const theme = RecentlyViewed.getProducts(); // max 4, current product at [0]
  let extended = [];
  try {
    const raw = localStorage.getItem(EXTENDED_KEY);
    if (raw) extended = JSON.parse(raw);
  } catch {}

  // Merge: theme list first (preserves current-product-at-0 order),
  // then any extended IDs not already in the theme list
  const merged = [
    ...theme,
    ...extended.filter((id) => !theme.includes(id)),
  ].slice(0, EXTENDED_MAX);

  return merged.length ? merged : theme;
}

// ─── Section rendering ────────────────────────────────────────────────────────
const controllers = new Map();

async function renderRecentlyViewedSection(sectionId, container) {
  const allIds = getOrderedIds();

  if (!allIds.length) {
    container.setAttribute('hidden', '');
    return;
  }

  // Skip the current product (index 0) client-side.
  // Fetch all maxItems IDs so pagination page 2 has content.
  // --slide-width is set to show exactly `columns` per page.
  const offsetEnabled = container.dataset.offsetCurrentProduct !== 'false';
  const columns = parseInt(container.dataset.columns || '6', 10);
  const maxItems = parseInt(container.dataset.maxItems || '8', 10);
  const ids = offsetEnabled ? allIds.slice(1, maxItems + 1) : allIds.slice(0, maxItems);

  if (!ids.length) {
    container.setAttribute('hidden', '');
    return;
  }

  controllers.get(sectionId)?.abort();
  const controller = new AbortController();
  controllers.set(sectionId, controller);

  const searchTerms = ids.map((id) => `id:${id}`).join(' OR ');
  const params = new URLSearchParams({
    q: searchTerms,
    type: 'product',
    limit: '10',
    section_id: sectionId,
  });
  const url = `/search?${params.toString()}&options[prefix]=last`;

  try {
    const response = await fetch(url, { signal: controller.signal });
    if (!response.ok) {
      console.error('[RecentlyViewed] Fetch failed:', response.status, url);
      return;
    }

    const html = await response.text();
    // Count how many products came back
    const productMatches = (html.match(/data-resource-type="product"/g) || []).length;

    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    // Shopify wraps the section in <div id="shopify-section-{id}">
    const rendered = doc.querySelector(`#shopify-section-${sectionId}`);
    if (!rendered) {
      console.error('[RecentlyViewed] Section wrapper not found in response. Available IDs:',
        [...doc.querySelectorAll('[id^="shopify-section"]')].map(el => el.id));
      return;
    }

    // Log whether search.performed fired (products will be present if so)
    const hasCarousel = rendered.querySelector('.recently-viewed-section__list');

    // Inside the outer wrapper is our [data-recently-viewed-section] div.
    // We want that div's *innerHTML* since container IS that element.
    const inner = rendered.querySelector('[data-recently-viewed-section]');
    const source = inner || rendered;

    // innerHTML does not trigger connectedCallback on custom elements
    // (e.g. slideshow-component, product-card-link). We must use adoptNode
    // + replaceChildren so the browser properly upgrades every custom element.
    //
    // Also move any <style> tags to <head> — styles injected via innerHTML
    // are parsed but never applied as active stylesheets.

    // 1. Hoist styles to <head> before touching the container
    source.querySelectorAll('style').forEach((s) => {
      // Avoid duplicating styles that are already in <head>
      const existing = document.head.querySelector(`style[data-rv="${s.dataset.rv || ''}"]`);
      if (!existing || !s.dataset.rv) {
        const live = document.createElement('style');
        if (s.dataset.rv) live.dataset.rv = s.dataset.rv;
        live.textContent = s.textContent;
        document.head.appendChild(live);
      }
      s.remove();
    });

    // 2. Purge any IDs from extended storage that search didn't return.
    //    This keeps the pool fresh — deleted/unpublished products are removed
    //    automatically so they don't occupy slots on future page loads.
    try {
      const returnedIds = new Set(
        [...doc.querySelectorAll('[data-product-id]')]
          .map(el => el.dataset.productId)
          .filter(Boolean)
      );
      const stored = JSON.parse(localStorage.getItem(EXTENDED_KEY) || '[]');
      // Only purge IDs that were actually searched but not returned
      const searched = new Set(ids);
      const purged = stored.filter(id => !searched.has(id) || returnedIds.has(id));
      if (purged.length !== stored.length) {
        _internalWrite = true;
        localStorage.setItem(EXTENDED_KEY, JSON.stringify(purged));
        _internalWrite = false;
      }
    } catch {}

    // 3. Adopt all child nodes from the parsed document into this document
    //    so custom element constructors and connectedCallback fire correctly
    const adopted = document.adoptNode(source);

    // 4. Replace container contents with adopted nodes
    container.replaceChildren(...adopted.childNodes);
    container.removeAttribute('hidden');
    container.dataset.rendered = 'true';

    // Force --slide-width on the slideshow-component element directly.
    // We calculate from the actual rendered container width so the slides
    // fill exactly 1/N of the visible area regardless of padding or margins.
    const slideshowEl = container.querySelector('slideshow-component');
    const wrapperEl = container.querySelector('.recently-viewed-section__carousel-wrapper');
    if (slideshowEl && wrapperEl) {
      const setSlideWidth = () => {
        const containerWidth = wrapperEl.getBoundingClientRect().width;
        const isMobile = window.innerWidth < 750;
        if (isMobile) {
          // On mobile show 1.5 cards — the half-visible card signals scrollability.
          // 1.5 columns with a small gap on each side.
          const mobileColumns = 1.5;
          const gap = 8;
          const slideWidth = Math.floor((containerWidth - gap) / mobileColumns);
          slideshowEl.style.setProperty('--slide-width', `${slideWidth}px`);
        } else {
          const gap = parseInt(getComputedStyle(wrapperEl).getPropertyValue('--resource-list-column-gap-desktop') || '8', 10);
          const totalGap = gap * (columns - 1);
          const slideWidth = Math.floor((containerWidth - totalGap) / columns);
          slideshowEl.style.setProperty('--slide-width', `${slideWidth}px`);
        }
      };
      setSlideWidth();
      // Re-calculate on resize (handles orientation change and window resize)
      new ResizeObserver(setSlideWidth).observe(wrapperEl);
    }
  } catch (err) {
    if (err.name === 'AbortError') return;
    console.error('[RecentlyViewed] Section render failed:', err);
  } finally {
    controllers.delete(sectionId);
  }
}

// ─── Auto-init ────────────────────────────────────────────────────────────────
// Defer slightly so the theme's addProduct() call for the current page fires
// first, updating both storage lists before we read them for the fetch.
// This ensures the offset_current_product logic works correctly on first load.
document.querySelectorAll('[data-recently-viewed-section]').forEach((section) => {
  const sectionId = section.dataset.sectionId;
  if (!sectionId) return;

  // Use requestAnimationFrame + setTimeout(0) to push our render after
  // all synchronous scripts on the page (including the theme's addProduct
  // inline script) have completed.
  requestAnimationFrame(() => {
    setTimeout(() => {
      renderRecentlyViewedSection(sectionId, section);
    }, 0);
  });
});